import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { ory } from 'src/providers/authProvider';
import { Logger } from 'src/utils/Logger';
import PageLoader from 'src/UI/loader/PageLoader';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { RecoveryPage } from 'src/auth/recovery/RecoveryPage';

type RouteState = 'checking' | 'recovery' | 'legacy_recovery' | 'error';

const AUTH_METHOD_LINK_RECOVERY = 'link_recovery';

export const RecoveryPageWithFlag = () => {
  const { flagEnabledCustomSettingsFlow } = useFlags();
  const [routeState, setRouteState] = useState<RouteState>('checking');
  const isRedirectingRef = useRef(false);
  const [searchParams] = useSearchParams();
  const flowIdParam = searchParams.get('flow');

  useEffect(() => {
    let cancelled = false;

    const determineRoute = async () => {
      if (isRedirectingRef.current) return;

      try {
        const { data: session } = await ory.toSession();
        const authMethods = session.authentication_methods ?? [];
        const mostRecentMethod = authMethods[authMethods.length - 1]?.method;

        if (mostRecentMethod === AUTH_METHOD_LINK_RECOVERY) {
          if (isRedirectingRef.current) return;
          isRedirectingRef.current = true;
          const { data: settingsFlow } = await ory.createBrowserSettingsFlow();
          window.location.replace(`/invite?flow=${settingsFlow.id}`);
          return;
        }
      } catch {
        Logger.info('No active session, proceeding with recovery flow');
      }

      if (cancelled || isRedirectingRef.current) return;

      if (flowIdParam) {
        try {
          const { data: flow } = await ory.getRecoveryFlow({ id: flowIdParam });
          if (cancelled || isRedirectingRef.current) return;
          if (flow.active === 'link') {
            isRedirectingRef.current = true;
            window.location.replace('/invite?expired=true');
            return;
          }
        } catch {
          Logger.info('Failed to fetch recovery flow, proceeding with normal flow');
        }
      }

      if (cancelled || isRedirectingRef.current) return;

      if (flagEnabledCustomSettingsFlow) {
        setRouteState('recovery');
      } else {
        try {
          isRedirectingRef.current = true;
          const { data } = await ory.createBrowserRecoveryFlow();
          window.location.replace(data.request_url);
        } catch {
          isRedirectingRef.current = false;
          if (!cancelled) setRouteState('error');
        }
      }
    };

    determineRoute();

    return () => {
      cancelled = true;
    };
  }, [flagEnabledCustomSettingsFlow, flowIdParam]);

  if (routeState === 'checking') {
    return <PageLoader />;
  }

  if (routeState === 'error') {
    return <GenericErrorPage error={new Error('Failed creating browser recovery flow')} />;
  }

  return <RecoveryPage />;
};
